import { useEffect, useState } from 'react'

import SmallSpinner from '@/components/shared/spinners/small-spinner'
import { APP_NAME } from '@/lib/constants'
import { setSession, type Session } from '@/lib/storage'
import { exchangeCodeForTokens } from '@/lib/auth/gravitee'

// Anti-boucle infinie sur les 401 Gravitee → PASS (id_token invalide, user PASS
// introuvable, etc.). Sans ce garde-fou, Gravitee garde une session SSO et re-émet
// un code à chaque /authorize → le front re-échange → même échec → boucle.
const ATTEMPT_STORAGE_KEY = 'gravitee_callback_attempts'
const MAX_ATTEMPTS = 2

const GraviteeCallback = () => {
    const [error, setError] = useState<string | null>(null)
    const [isFatal, setIsFatal] = useState(false)

    useEffect(() => {
        const handleCallback = async () => {
            const params = new URLSearchParams(window.location.search)
            const code = params.get('code')
            const errorParam = params.get('error')

            if (errorParam) {
                setError(`Erreur d'authentification : ${params.get('error_description') || errorParam}`)
                return
            }

            if (!code) {
                setError('Code d\'autorisation manquant')
                return
            }

            const state = params.get('state')

            try {
                const result = await exchangeCodeForTokens(code, state)

                // Succès — reset le compteur de tentatives
                sessionStorage.removeItem(ATTEMPT_STORAGE_KEY)

                // Redirect cross-brand : PASS détermine que le client doit aller
                // sur l'espace d'une autre marque et a généré un OTT. L'URL est
                // de la forme `https://target.com#ott=<token>`. Côté target,
                // App.jsx détecte le fragment, échange l'OTT et pose le cookie
                // PASS classique — pas besoin de repasser par Gravitee.
                if (result.redirect_to) {
                    window.location.href = result.redirect_to
                    return
                }

                // Les tokens (access_token, refresh_token) sont dans des cookies HttpOnly
                // On stocke uniquement le flag authenticated + id_token (pour le logout hint)
                const storage: Session = {
                    authenticated: true,
                    id_token: result.id_token,
                }
                setSession(storage)

                // Redirect vers l'URL d'origine ou la home
                const urlOrigin = localStorage.getItem(`url-${APP_NAME}`)
                localStorage.removeItem(`url-${APP_NAME}`)

                // Validation anti-open redirect : seules les URLs de même origine sont autorisées
                if (urlOrigin) {
                    try {
                        const url = new URL(urlOrigin)
                        if (url.origin === window.location.origin) {
                            window.location.href = urlOrigin
                            return
                        }
                    } catch {
                        // URL invalide — fallback sur /
                    }
                }
                window.location.href = '/'
            } catch (err) {
                console.error('Erreur callback Gravitee AM:', err)

                // Incrémente le compteur avant de décider quoi faire
                const attempts = parseInt(sessionStorage.getItem(ATTEMPT_STORAGE_KEY) || '0', 10) + 1
                sessionStorage.setItem(ATTEMPT_STORAGE_KEY, String(attempts))

                if (attempts >= MAX_ATTEMPTS) {
                    // Boucle détectée — on stoppe et on demande à l'user de contacter le support
                    sessionStorage.removeItem(ATTEMPT_STORAGE_KEY)
                    setIsFatal(true)
                    setError('Impossible d\'accéder à votre espace après plusieurs tentatives. Merci de contacter le support.')
                    return
                }

                // Au retry, forcer Gravitee à redemander les identifiants
                // (casse la session SSO qui renvoie le même code sur boucle).
                sessionStorage.setItem('gravitee_force_login', 'true')
                setError((err instanceof Error && err.message) || 'Erreur lors de l\'authentification')
            }
        }

        void handleCallback()
    }, [])

    if (error) {
        return (
            <div className="text-center">
                <p className="text-destructive mb-4">{error}</p>
                {!isFatal && (
                    <a href="/login" className="inline-flex items-center justify-center gap-2 px-5 py-2.5 text-[0.875rem]/5 font-medium rounded-md transition-all ease-[ease] cursor-pointer no-underline disabled:opacity-50 disabled:pointer-events-none bg-primary text-primary-foreground border-none hover:bg-primary-hover">
                        Retour à la connexion
                    </a>
                )}
            </div>
        )
    }

    return (
        <div className="text-center">
            <SmallSpinner />
            <p className="mt-4">Authentification en cours...</p>
        </div>
    )
}

export default GraviteeCallback
